import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RecordCardField {
  label: string;
  value: ReactNode;
}

export interface RecordCardAction {
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  href?: string;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export interface RecordCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  fields?: RecordCardField[];
  actions?: RecordCardAction[];
  /** Si se pasa, el título se vuelve accionable (card navegable) sin anidar interactivos. */
  onClick?: () => void;
}

/**
 * Card de registro para el layout móvil de las tablas de listado (UX-RESP-01).
 * Reemplaza la fila de tabla en `< md`. Tap targets ≥44px (min-h-11), tokens,
 * sin anidar elementos interactivos (a11y). Ver spec 2026-08-07-mobile-card-layout.
 */
export function RecordCard({ title, subtitle, badge, fields, actions, onClick }: RecordCardProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {onClick ? (
            <button
              type="button"
              onClick={onClick}
              className="block text-left text-sm font-semibold text-card-foreground hover:underline"
            >
              {title}
            </button>
          ) : (
            <p className="text-sm font-semibold text-card-foreground">{title}</p>
          )}
          {subtitle != null && subtitle !== '' && (
            <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {badge != null && <div className="shrink-0">{badge}</div>}
      </div>

      {fields && fields.length > 0 && (
        <dl className="mt-3 space-y-1.5 border-t border-border pt-3">
          {fields.map((f) => (
            <div key={f.label} className="flex items-start justify-between gap-3 text-sm">
              <dt className="shrink-0 text-muted-foreground">{f.label}</dt>
              <dd className="min-w-0 text-right text-card-foreground">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {actions && actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
          {actions.map((a) => {
            const cls = cn(
              'inline-flex min-h-11 flex-1 basis-24 items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors',
              a.variant === 'danger'
                ? 'border-destructive/30 text-destructive hover:bg-destructive/10'
                : 'border-border text-foreground hover:bg-accent',
              a.disabled && 'cursor-not-allowed opacity-50',
            );
            const content = (
              <>
                {a.icon && <a.icon className="h-4 w-4" aria-hidden="true" />}
                {a.label}
              </>
            );
            return a.href ? (
              <a
                key={a.label}
                href={a.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cls}
              >
                {content}
              </a>
            ) : (
              <button
                key={a.label}
                type="button"
                onClick={a.onClick}
                disabled={a.disabled}
                className={cls}
              >
                {content}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
